import React, { Dispatch, memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { CarPark } from "../../../../services";
import useModelSubscribe from "../../../../hooks/useModelSubscribe2";
import { useTaxiServiceFilterAccessControl } from "../../../../hooks";
import MultiSelectWithModalBase, {
	MultiSelectWithModal,
} from "../MultiSelectWithModal";

const MultiSelectCarParkWithModalBase: React.FC<
	MultiSelectCarParkWithModalBase.Props
> = ({
	value,
	onChange,
	disabled,
	titleText,
	required,
	modalSetting,
	sort,
	allowedTaxiServiceIds,
	all = true,
	accessIds,
	showLable,
	showSelectAll = true,
	fleetIdToNameSettings,
	modelsFromCarPark,
	...props
}) => {
	const { t } = useTranslation();

	const { accessTaxiServicesIds } = useTaxiServiceFilterAccessControl<any>();

	const optionsSubscribe: CarPark.SubscribeOptions = useMemo(
		() => ({
			...(allowedTaxiServiceIds || accessTaxiServicesIds
				? {
						taxiServiceIds:
							allowedTaxiServiceIds || accessTaxiServicesIds,
				  }
				: {}),
		}),
		[accessTaxiServicesIds, allowedTaxiServiceIds],
	);

	const subscribe = useModelSubscribe(optionsSubscribe, CarPark);

	const items = useMemo(() => {
		const source = modelsFromCarPark ?? subscribe.models ?? [];
		const keepIds = new Set(value);
		let retval = source;

		if (Array.isArray(accessIds)) {
			retval = retval.filter(
				(item) => accessIds.includes(item.id) || keepIds.has(item.id),
			);
		}

		if (allowedTaxiServiceIds?.length) {
			retval = retval.filter((item) => {
				if (keepIds.has(item.id)) return true;
				const taxiIds = item.taxiServices?.map((service) => service.id);
				if (!taxiIds?.length) return true;
				return taxiIds.some((id) => allowedTaxiServiceIds.includes(id));
			});
		}

		keepIds.forEach((id) => {
			if (typeof id !== "number") return;
			if (retval.some((item) => item.id === id)) return;
			const found = source.find((item) => item.id === id);
			if (found) retval = [...retval, found];
		});

		return retval;
	}, [
		accessIds,
		allowedTaxiServiceIds,
		modelsFromCarPark,
		subscribe.models,
		value,
	]);

	const [error, setError] = useState("");
	const [initialValue] = useState(value);

	const selectOptions = useMemo<MultiSelectWithModalBase.Value>(() => {
		if (fleetIdToNameSettings) {
			const disabledOptiond = { ...fleetIdToNameSettings };

			if (Array.isArray(initialValue) && initialValue.length > 0) {
				initialValue.forEach((id) => {
					if (id in disabledOptiond) {
						delete disabledOptiond[id];
					}
				});
			}

			const retval2 = items.map((item) => ({
				key: item.id,
				label: item.name,
				value: item.id,
				name: item.name,
				data: item,
				disabled: !!disabledOptiond?.[item.id],
				labelRigth: disabledOptiond?.[item.id],
			}));

			return retval2;
		}

		const retval = items.map((item) => ({
			key: item.id,
			label: item.name,
			value: item.id,
			name: item.name,
			data: item,
		}));
		return retval;
	}, [fleetIdToNameSettings, initialValue, items]);

	const selected = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			selectOptions.filter((item) =>
				value.find((data) => data === item.value),
			),

		[selectOptions, value],
	);

	const selectOnChange = useCallback(
		(newValue) => {
			setError("");
			const Ids = newValue.map((item) => item.value);
			onChange(Ids);
		},
		[onChange],
	);

	const texts = useMemo(
		() => ({
			title: t(titleText),
			allText: t(["all_car_parks"]),
			allActiveText: t(["all_active_car_parks"]),
		}),
		[t, titleText],
	);

	return (
		<MultiSelectWithModal
			error={error}
			disabled={disabled}
			value={selected}
			title={texts.title}
			allText={all ? texts.allText : undefined}
			allActiveText={texts.allActiveText}
			options={selectOptions}
			onChange={selectOnChange}
			required={required}
			pullUpItemInsideArray
			hiddenButton={false}
			showSelectAll={showSelectAll}
			modalSetting={modalSetting}
			sort={{
				show: true,
				active: true,
				...(sort || {}),
			}}
			showLable={showLable}
			{...props}
		/>
	);
};

export const MultiSelectCarParkWithModal = memo(
	MultiSelectCarParkWithModalBase,
);

declare namespace MultiSelectCarParkWithModalBase {
	interface Props extends MultiSelectWithModalBase.Setting {
		value: Value;
		onChange: Dispatch<Value>;
		titleText: string | string[];
		modalSetting?: MultiSelectWithModalBase.ListSelectProps["modalSetting"];
		setShowModalProps?: (value: boolean) => void;
		allowedTaxiServiceIds?: number[];
		all?: boolean;
		accessIds?: number[];
		showLable?: boolean;
		showSelectAll?: boolean;
		fleetIdToNameSettings?: Record<number, string>;
		modelsFromCarPark?: CarPark.Model[];
	}

	type Value = number[];
}

export default MultiSelectCarParkWithModalBase;
